import { c as _c } from "react/compiler-runtime";
import { formatWorkspaceRoleInSentence, getAccessVerb, getGroupAccess, hasAccessToAllProjects, HIDE_ALERT_ICON_SX } from "@components/common/member/member-access-utils";
import { Alert } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { getPrettyName } from "@utils/user-utils";

/**
 * Explains, for members whose workspace role reaches every project, that the
 * groups listed on their profile are only the explicit assignments.
 */
export function MemberAccessSummary(t0) {
  const $ = _c(23);
  const {
    member
  } = t0;
  if (!hasAccessToAllProjects(member.role)) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      fontWeight: "inherit"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== member) {
    t2 = getPrettyName(member);
    $[1] = member;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <Box component="var" sx={t1}>{t2}</Box>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== member.role) {
    t4 = getAccessVerb(member.role);
    $[5] = member.role;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <>{t3}{" "}can {t4} every project in this workspace</>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== member.role) {
    t6 = formatWorkspaceRoleInSentence(member.role);
    $[10] = member.role;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== member) {
    t7 = getPrettyName(member);
    $[12] = member;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <var>{t7}</var>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t6 || $[17] !== t8) {
    t9 = <>The {t6} role gives access to every project, assigned or not. Only groups where{" "}{t8} has a group role or an assigned project are listed below.</>;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      mb: 2
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t5 || $[21] !== t9) {
    t11 = <Alert variant="info" title={t5} description={t9} sx={t10} />;
    $[20] = t5;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}
/** Explains through a group role how the member reaches the group's projects */
function GroupRoleNoteText(t0) {
  const $ = _c(11);
  const {
    member,
    group,
    hasAssignedProjects
  } = t0;
  let t1;
  if ($[0] !== group.role) {
    t1 = getAccessVerb(group.role);
    $[0] = group.role;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const verb = t1;
  let t2;
  if ($[2] !== group.role) {
    t2 = getGroupAccess(group.role).label.toLowerCase();
    $[2] = group.role;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const role = t2;
  let t3;
  if ($[4] !== member) {
    t3 = getPrettyName(member);
    $[4] = member;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <var>{t3}</var>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = hasAssignedProjects ? `can also ${verb} the other projects in this group as ${role}.` : `can ${verb} all projects in this group as ${role}.`;
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <>{t4}{" "}{t5}</>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

/** Explains through the workspace role how the member reaches the group's projects */
function WorkspaceRoleNoteText(t0) {
  const $ = _c(11);
  const {
    member,
    hasAssignedProjects
  } = t0;
  let t1;
  if ($[0] !== member.role) {
    t1 = getAccessVerb(member.role);
    $[0] = member.role;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const verb = t1;
  let t2;
  if ($[2] !== member.role) {
    t2 = formatWorkspaceRoleInSentence(member.role);
    $[2] = member.role;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const role = t2;
  let t3;
  if ($[4] !== member) {
    t3 = getPrettyName(member);
    $[4] = member;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <var>{t3}</var>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = hasAssignedProjects ? `isn't assigned to the other projects in this group, but can still ${verb} them as ${role}.` : `can still ${verb} all projects in this group as ${role}.`;
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <>{t4}{" "}{t5}</>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

/**
 * Note under a group's projects that the member also reaches the group's other,
 * unassigned projects, through a group role or an all-projects workspace role.
 */
export function MemberGroupOtherProjectsNote(t0) {
  const $ = _c(8);
  const {
    member,
    group,
    hasAssignedProjects
  } = t0;
  const hasGroupWideRole = group.role === "COMPANY_MANAGER" || group.role === "GROUP_VIEWER";
  if (!hasGroupWideRole && !hasAccessToAllProjects(member.role)) {
    return null;
  }
  let t1;
  if ($[0] !== group || $[1] !== hasAssignedProjects || $[2] !== hasGroupWideRole || $[3] !== member) {
    t1 = hasGroupWideRole ? <GroupRoleNoteText member={member} group={group} hasAssignedProjects={hasAssignedProjects} /> : <WorkspaceRoleNoteText member={member} hasAssignedProjects={hasAssignedProjects} />;
    $[0] = group;
    $[1] = hasAssignedProjects;
    $[2] = hasGroupWideRole;
    $[3] = member;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ...HIDE_ALERT_ICON_SX,
      mt: 1.5,
      mx: 1.5
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1) {
    t3 = <Alert variant="neutral" title="" description={t1} sx={t2} />;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}